import {groupBy, isEmpty, map, sortBy, uniqBy} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi} from 'api';
import {Button, DataWrapper} from 'components';
import {NO_TEXT_CHAT_HISTORY, TEXT_CHAT_HISTORY_ERROR} from 'constants/images';
import {UserContext, UserSessionContext} from 'context';
import {useApiData, useObservable, useUserSession} from 'hooks';
import {SessionHistory} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {authDataStore} from 'services';
import {formatDate} from 'utils/transform';

import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

import {TextChatHistoryAccordion} from './TextChatHistoryAccordion';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.TEXT_CHAT_HISTORY_SIDEBAR;

export const TextChatHistorySidebar: React.FC = () => {
    const {t} = useTranslation();
    const {localUser} = useObservable(authDataStore);
    const {sidebarView} = useContext(UserSessionContext);
    const {sessionParams} = useUserSession();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);
    const [fetchRequired, setFetchRequired] = useState(true);

    const {
        send: requestTextChatHistoryFn,
        data: textChatHistory,
        loading: textChatHistoryLoading,
        error: textChatHistoryError
    } = useApiData(agentActionsApi.getTextChatHistory);

    const requestTextChatHistory = useCallback((p: number) => {
        requestTextChatHistoryFn(sessionParams, p);
        setFetchRequired(false);
    }, [requestTextChatHistoryFn, sessionParams]);

    const [allSessionHistories, setAllSessionHistories] = useState<SessionHistory[]>([]);
    const groupedByDateSessionHistories = useMemo(() =>
        groupBy(allSessionHistories, (sessionHistory) =>
            formatDate(sessionHistory.startDate, timeZone, dateFormat)), [allSessionHistories, timeZone, dateFormat]);

    const [page, setPage] = useState(1);
    const handleLoadMore = useCallback(() => {
        setFetchRequired(true);
        setPage((prev) => ++prev);
    }, []);

    useEffect(() => {
        if (fetchRequired && sidebarView === SidebarView.TextChatHistory) {
            requestTextChatHistory?.(page);
        }
    }, [requestTextChatHistory, page, sidebarView, fetchRequired]);

    useEffect(() => {
        if (!isEmpty(textChatHistory?.items)) {
            setAllSessionHistories((prev) => {
                const uniqueItems = uniqBy([...textChatHistory.items, ...prev], 'sessionId');
                return sortBy(uniqueItems, (item) => new Date(item.startDate));
            });
        }
    }, [textChatHistory]);

    return (
        <SidebarContent
            view={SidebarView.TextChatHistory}
            header={<div className='tw-ts-h5'>{t(T_KEYS.HEADER)}</div>}
        >
            {!textChatHistoryError && !isEmpty(groupedByDateSessionHistories) && (textChatHistory.page < textChatHistory.pages) &&
                <div className='tw-text-center tw-my-4'><Button onClick={handleLoadMore}>{t(T_KEYS.LOAD_MORE)}</Button></div>}
            {!textChatHistoryLoading && !textChatHistoryError && isEmpty(groupedByDateSessionHistories) &&
                <div className='tw-relative tw-h-full'>
                    <div className='tw-inset-0 tw-flex-center tw-absolute tw-flex-col'>
                        <img src={NO_TEXT_CHAT_HISTORY} />
                        <div className='tw-mt-4'>{t(T_KEYS.NO_HISTORY)}</div>
                    </div>
                </div>}
            {!textChatHistoryLoading && textChatHistoryError &&
                <div className='tw-relative tw-h-full'>
                    <div className='tw-inset-0 tw-flex-center tw-absolute tw-flex-col'>
                        <img src={TEXT_CHAT_HISTORY_ERROR} />
                        <div className='tw-my-4'>{t(T_KEYS.ERROR)}</div>
                        <Button onClick={() => requestTextChatHistory?.(page)}>{t(T_KEYS.TRY_AGAIN)}</Button>
                    </div>
                </div>}
            <DataWrapper isLoading={textChatHistoryLoading} hideContentOnLoading={false}>
                {map(groupedByDateSessionHistories, (histories, date) => (
                    <Fragment key={date}>
                        <div className='tw-text-center tw-my-4'>{date}</div>
                        {map(histories, (sessionHistory) => (
                            <TextChatHistoryAccordion
                                key={sessionHistory.sessionId}
                                localUserEid={localUser.eid}
                                sessionHistory={sessionHistory}
                                className='tw-my-2 tw-rounded-xl tw-border tw-border-default'
                            />
                        ))}
                    </Fragment>
                ))}
            </DataWrapper>
        </SidebarContent>
    );
};